import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import SearchableSelect from '../common/SearchableSelect';
import { formatCurrency } from '../../utils/formatters';

const AICategorizationModal = ({ show, onHide, activeProject, accounts, fetchTransactions }) => {
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    
    const [step, setStep] = useState('select'); // 'select', 'processing', 'review'
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const handleTrigger = async () => {
        setLoading(true);
        setError(null);
        try {
            // 1. Trigger the background worker
            await axios.post(`/api/projects/${activeProject._id}/ai-categorization/trigger`, {
                month: selectedMonth,
                year: selectedYear
            });
            
            setStep('processing');
            startPolling();
        } catch (err) {
            setError(err.response?.data?.msg || 'Failed to trigger AI analysis');
            setLoading(false);
        }
    };

    const startPolling = () => {
        const interval = setInterval(async () => {
            try {
                const res = await axios.get(`/api/projects/${activeProject._id}/ai-categorization/queue`, {
                    params: { month: selectedMonth, year: selectedYear }
                });
                
                // If any transactions in the queue have aiSuggestedAccount, we can show review
                const hasSuggestions = res.data.queue.some(tx => tx.aiSuggestedAccount);
                if (hasSuggestions) {
                    setQueue(res.data.queue.filter(tx => tx.aiSuggestedAccount));
                    setStep('review');
                    setLoading(false);
                    clearInterval(interval);
                }
            } catch (err) {
                console.error('Polling error:', err);
            }
        }, 3000);

        // Timeout after 60 seconds
        setTimeout(() => {
            clearInterval(interval);
            if (step === 'processing') {
                setError('AI analysis is taking longer than expected. Please check back later.');
                setLoading(false);
            }
        }, 60000);
    };

    const handleUpdateSuggestion = (txId, accountId) => {
        setQueue(prev => prev.map(tx => 
            tx._id === txId ? { ...tx, aiSuggestedAccount: accountId, isEdited: true } : tx
        ));
    };

    const handleFinalize = async () => {
        setLoading(true);
        try {
            const updates = queue.map(tx => ({
                transactionId: tx._id,
                accountId: tx.aiSuggestedAccount._id || tx.aiSuggestedAccount,
                status: tx.isEdited ? 'edited' : 'approved'
            }));

            await axios.post(`/api/projects/${activeProject._id}/ai-categorization/finalize`, { updates });
            
            setSuccess(`Successfully categorized ${updates.length} transactions!`);
            fetchTransactions(); // Refresh main list
            setTimeout(() => {
                onHide();
                setStep('select');
                setSuccess(null);
            }, 2000);
        } catch (err) {
            setError('Failed to finalize categorization');
        } finally {
            setLoading(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case 'select':
                return (
                    <div className="p-4 text-center">
                        <i className="fas fa-magic fa-4x text-info mb-4"></i>
                        <h4>AI Auto-Categorization</h4>
                        <p className="text-muted">Select a month to have AI analyze and suggest categories for your transactions based on your history.</p>
                        
                        <Row className="justify-content-center mt-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small font-weight-bold">Select Period</Form.Label>
                                    <div className="d-flex">
                                        <Form.Control 
                                            as="select" 
                                            className="mr-2"
                                            value={selectedMonth}
                                            onChange={e => setSelectedMonth(parseInt(e.target.value))}
                                        >
                                            {Array.from({ length: 12 }, (_, i) => (
                                                <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</option>
                                            ))}
                                        </Form.Control>
                                        <Form.Control 
                                            type="number" 
                                            value={selectedYear}
                                            onChange={e => setSelectedYear(parseInt(e.target.value))}
                                        />
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Button 
                            variant="primary" 
                            className="mt-4 px-5"
                            onClick={handleTrigger}
                            disabled={loading}
                        >
                            {loading ? <><Spinner animation="border" size="sm" /> Analyzing...</> : 'Analyze Transactions'}
                        </Button>
                    </div>
                );

            case 'processing':
                return (
                    <div className="p-5 text-center">
                        <Spinner animation="grow" variant="info" className="mb-4" style={{ width: '3rem', height: '3rem' }} />
                        <h5>Gemini is thinking...</h5>
                        <p className="text-muted">Analyzing your Chart of Accounts and historical patterns to suggest the best categories for this month.</p>
                    </div>
                );

            case 'review':
                return (
                    <div>
                        <div className="alert alert-info py-2 small mb-4">
                            <i className="fas fa-info-circle mr-2"></i>
                            AI has suggested categories for <strong>{queue.length}</strong> transactions. Please review and edit before finalizing.
                        </div>

                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <Table responsive hover size="sm" className="border-bottom">
                                <thead className="bg-light sticky-top">
                                    <tr>
                                        <th className="small font-weight-bold py-2">Date</th>
                                        <th className="small font-weight-bold py-2">Description</th>
                                        <th className="small font-weight-bold py-2 text-right">Amount</th>
                                        <th className="small font-weight-bold py-2" style={{ width: '250px' }}>AI Suggested Category</th>
                                        <th className="small font-weight-bold py-2 text-center">Confidence</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {queue.map(tx => (
                                        <tr key={tx._id}>
                                            <td className="small align-middle">{new Date(tx.date).toLocaleDateString()}</td>
                                            <td className="small align-middle">
                                                <div className="font-weight-bold">{tx.description}</div>
                                                <div className="extra-small text-muted text-truncate" style={{ maxWidth: '200px' }}>{tx.rawDescription}</div>
                                            </td>
                                            <td className="small align-middle text-right">{formatCurrency(tx.amount)}</td>
                                            <td className="align-middle">
                                                <SearchableSelect 
                                                    size="sm"
                                                    options={accounts.map(a => ({ value: a._id, label: a.name, sublabel: a.type }))}
                                                    value={tx.aiSuggestedAccount._id || tx.aiSuggestedAccount}
                                                    onChange={e => handleUpdateSuggestion(tx._id, e.target.value)}
                                                />
                                            </td>
                                            <td className="align-middle text-center">
                                                <Badge variant={tx.aiConfidence > 80 ? 'success' : tx.aiConfidence > 60 ? 'warning' : 'danger'}>
                                                    {tx.aiConfidence}%
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-4 border-top pt-3">
                            <div className="small text-muted">
                                {queue.filter(tx => tx.isEdited).length} edits made
                            </div>
                            <div>
                                <Button variant="link" size="sm" onClick={() => setStep('select')} className="mr-3">Restart</Button>
                                <Button variant="success" className="px-4" onClick={handleFinalize} disabled={loading}>
                                    {loading ? 'Processing...' : 'Finalize & Update Ledger'}
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            default: return null;
        }
    };

    return (
        <Modal show={show} onHide={onHide} size={step === 'review' ? 'lg' : 'md'} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title className="h5">AI Smart Categorization</Modal.Title>
            </Modal.Header>
            <Modal.Body className={step === 'review' ? 'p-0' : 'p-4'}>
                {error && <Alert variant="danger" className="mx-3 mt-3 small py-2">{error}</Alert>}
                {success && <Alert variant="success" className="mx-3 mt-3 small py-2">{success}</Alert>}
                
                <div className={step === 'review' ? 'px-4 py-3' : ''}>
                    {renderStep()}
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default AICategorizationModal;
